import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faFileUpload, faTimes, faDownload } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import starimg from '../../public/customerstar.jpg';

const AdminCustomerDetails = () => {
  const [customersData, setCustomersData] = useState([]);
  const [salesAgentsData, setSalesAgentsData] = useState([]);
  const [salesSupervisorsData, setSalesSupervisorsData] = useState([]);
  const [businessManagersData, setBusinessManagersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeView, setActiveView] = useState('customers');
  const navigate = useNavigate();

  const [loadingStates, setLoadingStates] = useState({
    deactivate: {},
    delete: {}
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalRole, setModalRole] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: modalRole,
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const downloadExcel = async () => {
    const downloadPromise = new Promise(async (resolve, reject) => {
      try {
        setIsDownloading(true);
        
        // Prepare the data for export
        const exportData = customersData.map(customer => ({
          'First Name': customer.firstName,
          'Last Name': customer.lastName,
          'Phone Number': customer.phoneNumber,
          'Gender': customer.gender,
          'Store Name': customer.storeName,
          'Store Type': customer.storeType,
          'State': customer.state,
          'LGA': customer.lga,
          'City': customer.city,
          'Store Address': customer.storeAddress,
          'Locality': customer.locality,
          'GPS Code': customer.gpsCode,
          'Potential Purchase': customer.potentialpurchase,
          'Customer Class': customer.customerclass,
          'Field Agent': customer.fieldagent || 'Not Assigned',
          'Email': customer.email || 'Not Provided',
          'Customer Visit Days': customer.customervisitdays || 'Not Set',
          'Date Added': customer.createdAt
        }));

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Add some styling
        const colWidths = [
          { wch: 15 }, // First Name
          { wch: 15 }, // Last Name
          { wch: 15 }, // Phone Number
          { wch: 10 }, // Gender
          { wch: 20 }, // Store Name
          { wch: 15 }, // Store Type
          { wch: 15 }, // State
          { wch: 15 }, // LGA
          { wch: 15 }, // City
          { wch: 30 }, // Store Address
          { wch: 15 }, // Locality
          { wch: 15 }, // GPS Code
          { wch: 20 }, // Potential Purchase
          { wch: 15 }, // Customer Class
          { wch: 20 }, // Field Agent
          { wch: 25 }, // Email
          { wch: 20 }, // Customer Visit Days
          { wch: 15 }  // Date Added
        ];
        ws['!cols'] = colWidths;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Customers");

        // Generate Excel file
        XLSX.writeFile(wb, `Customers_Data_${new Date().toISOString().split('T')[0]}.xlsx`);

        resolve('Excel file downloaded successfully');
      } catch (error) {
        reject(error);
      } finally {
        setIsDownloading(false);
      }
    });

    toast.promise(downloadPromise, {
      loading: 'Downloading...',
      success: 'Excel file downloaded successfully',
      error: 'Failed to download Excel file'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitPromise = new Promise(async (resolve, reject) => {
      try {
        const response = await axios.post('https://citi-mobile-app.onrender.com/api/auth/admin/signup', formData);
        closeModal();
        // Refresh the relevant data list
        if (modalRole === 'fieldsalesagents') {
          await fetchUsersByRole('fieldsalesagents', setSalesAgentsData);
        } else if (modalRole === 'salessupervisors') {
          await fetchUsersByRole('salessupervisors', setSalesSupervisorsData);
        } else if (modalRole === 'businessmanager') {
          await fetchUsersByRole('businessmanager', setBusinessManagersData);
        }
        resolve(response.data.message);
      } catch (error) {
        reject(error.response?.data?.message || 'An error occurred');
      }
    });

    toast.promise(submitPromise, {
      loading: 'Adding user...',
      success: (message) => message || 'User added successfully',
      error: (err) => err || 'Failed to add user'
    });
  };

  const handleDeactivate = async (userId, role) => {
    setLoadingStates(prev => ({
      ...prev,
      deactivate: { ...prev.deactivate, [userId]: true }
    }));

    const deactivatePromise = new Promise(async (resolve, reject) => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const adminEmail = user?.email;

        const response = await axios.post(`https://citi-mobile-app.onrender.com/admin/deactivate/${userId}`, {
          adminEmail,
          role
        });

        if (response.data.success) {
          if (role === 'fieldsalesagents') {
            setSalesAgentsData(salesAgentsData.map(agent => 
              agent._id === userId ? { ...agent, active: !agent.active } : agent
            ));
          } else if (role === 'salessupervisors') {
            setSalesSupervisorsData(salesSupervisorsData.map(supervisor => 
              supervisor._id === userId ? { ...supervisor, active: !supervisor.active } : supervisor
            ));
          } else if (role === 'businessmanager') {
            setBusinessManagersData(businessManagersData.map(manager => 
              manager._id === userId ? { ...manager, active: !manager.active } : manager
            ));
          }
          resolve(response.data.message);
        }
      } catch (error) {
        reject(error.response?.data?.message || 'Failed to update status');
      } finally {
        setLoadingStates(prev => ({
          ...prev,
          deactivate: { ...prev.deactivate, [userId]: false }
        }));
      }
    });

    toast.promise(deactivatePromise, {
      loading: 'Updating status...',
      success: (message) => message || 'Status updated successfully',
      error: (err) => err || 'Failed to update status'
    });
  };

  const handleDelete = async (userId, role) => {
    setLoadingStates(prev => ({
      ...prev,
      delete: { ...prev.delete, [userId]: true }
    }));

    const deletePromise = new Promise(async (resolve, reject) => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const adminEmail = user?.email;

        const response = await axios.delete(`https://citi-mobile-app.onrender.com/admin/delete/${userId}`, {
          data: { adminEmail, role }
        });

        if (response.data.success) {
          if (role === 'fieldsalesagents') {
            setSalesAgentsData(salesAgentsData.filter(agent => agent._id !== userId));
          } else if (role === 'salessupervisors') {
            setSalesSupervisorsData(salesSupervisorsData.filter(supervisor => supervisor._id !== userId));
          } else if (role === 'businessmanager') {
            setBusinessManagersData(businessManagersData.filter(manager => manager._id !== userId));
          }
          resolve(response.data.message);
        }
      } catch (error) {
        reject(error.response?.data?.message || 'Failed to delete user');
      } finally {
        setLoadingStates(prev => ({
          ...prev,
          delete: { ...prev.delete, [userId]: false }
        }));
      }
    });

    toast.promise(deletePromise, {
      loading: 'Deleting user...',
      success: (message) => message || 'User deleted successfully',
      error: (err) => err || 'Failed to delete user'
    });
  };

  const openModal = (role) => {
    setModalRole(role);
    setFormData({...formData, role});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: '',
    });
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };



  // Fetch users by role function
  const fetchUsersByRole = async (role, setData) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const email = user?.email;

      if (!email) {
        console.error('User email not found in local storage');
        setLoading(false);
        return;
      }

      const response = await axios.post(`https://citi-mobile-app.onrender.com/admin/role-users`, { email, role });
      if (response.data.users) {
        setData(response.data.users);
      }
      setLoading(false);
    } catch (error) {
      console.error(`Error fetching ${role} data:`, error);
      setLoading(false);
      toast.error(`Error fetching ${role} data`);
    }
  };

  useEffect(() => {
    if (activeView === 'customers') {
      const fetchCustomersData = async () => {
        try {
          const user = JSON.parse(localStorage.getItem('user'));
          const email = user?.email;

          if (!email) {
            console.error('User email not found in local storage');
            setLoading(false);
            return;
          }

          const response = await axios.post('https://citi-mobile-app.onrender.com/admin/customers', { email });
          if (response.data.fullDetails) {
            setCustomersData(response.data.fullDetails);
          }
          setLoading(false);
        } catch (error) {
          console.error('Error fetching customer data:', error);
          setLoading(false);
          toast.error('Error fetching customer data');
        }
      };

      fetchCustomersData();
    } else if (activeView === 'salesAgent') {
      fetchUsersByRole('fieldsalesagents', setSalesAgentsData);
    } else if (activeView === 'salesSupervisor') {
      fetchUsersByRole('salessupervisors', setSalesSupervisorsData);
    } else if (activeView === 'businessManager') {
      fetchUsersByRole('businessmanager', setBusinessManagersData);
    }
  }, [activeView]);

  const handleViewAccount = (user) => {
    if (user && user._id) {
      navigate(`/AdminCustomerDetails/AdminViewCustomer/${user._id}`);
    }
  };


  const LoadingButton = ({ isLoading, onClick, className, children }) => {
    return (
      <button
        onClick={onClick}
        disabled={isLoading}
        className={`${className} relative ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
            Loading...
          </div>
        ) : (
          children
        )}
      </button>
    );
  };


  useEffect(() => {
    const fetchUsersByRole = async (role, setData) => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const email = user?.email;

        if (!email) {
          console.error('User email not found in local storage');
          setLoading(false);
          return;
        }

        const response = await axios.post(`https://citi-mobile-app.onrender.com/admin/role-users`, { email, role });
        console.log(`${role} data received:`, response.data);

        if (response.data.users) {
          setData(response.data.users);
        }
        setLoading(false);
      } catch (error) {
        console.error(`Error fetching ${role} data:`, error);
        setLoading(false);
      }
    };

    if (activeView === 'customers') {
      const fetchCustomersData = async () => {
        try {
          const user = JSON.parse(localStorage.getItem('user'));
          const email = user?.email;

          if (!email) {
            console.error('User email not found in local storage');
            setLoading(false);
            return;
          }

          const response = await axios.post('https://citi-mobile-app.onrender.com/admin/customers', { email });
          console.log('Customer data received:', response.data);

          if (response.data.fullDetails) {
            setCustomersData(response.data.fullDetails);
          }
          setLoading(false);
        } catch (error) {
          console.error('Error fetching customer data:', error);
          setLoading(false);
        }
      };

      fetchCustomersData();
    } else if (activeView === 'salesAgent') {
      fetchUsersByRole('fieldsalesagents', setSalesAgentsData);
    } else if (activeView === 'salesSupervisor') {
      fetchUsersByRole('salessupervisors', setSalesSupervisorsData);
    } else if (activeView === 'businessManager') {
      fetchUsersByRole('businessmanager', setBusinessManagersData);
    }
  }, [activeView]);






  return (
    <>
    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="colored"
    />
   
    <div className="ml-60 max-sm:ml-0 max-w-[80%] mt-20 max-sm:w-screen ">
      <div className="bg-[#FFFFFF] p-2 rounded-md mb-6">
        <div className="flex max-sm:flex-col justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">All customer-related information in one place</h1>
            <p className="text-gray-600">Easily manage customer details, view order history, and segment customers into groups.</p>
          </div>
          <img src={starimg} className="w-32 h-32" alt="Customer Star" />
        </div>

        <div className="flex max-sm:flex-col flex-wrap gap-4 mb-4">
          <Link to="/AdminCustomerDetails/AdminAddCustomer" className="bg-blue-600 text-white py-2 px-1 rounded flex items-center">
            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
            Add customer details
          </Link>
          <button
            onClick={() => openModal('fieldsalesagents')}
            className="bg-blue-600 text-white py-2 px-1 rounded flex items-center"
          >
            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
            Add Sales Agents
          </button>
          <button
            onClick={() => openModal('salessupervisors')}
            className="bg-blue-600 text-white py-2 px-1 rounded flex items-center"
          >
            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
            Add View Sales Supervisor
          </button>
          <button
            onClick={() => openModal('businessmanager')}
            className="bg-blue-600 text-white py-2 px-1 rounded flex items-center"
          >
            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
            Add Business Managers
          </button>
          <Link to="/AdminCustomerDetails/AdminImportCustomerDetails" className="border border-blue-600 text-blue-600 py-2 px-1 rounded flex items-center">
            <FontAwesomeIcon icon={faFileUpload} className="mr-2" />
            Bulk upload customer details
          </Link>
        </div>

        <div className="flex max-sm:flex-col gap-4 mb-4">
          <button
            onClick={() => setActiveView('customers')}
            className={`py-2 px-4 rounded flex items-center ${activeView === 'customers' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'}`}
          >
            View Customers
          </button>
          <button
            onClick={() => setActiveView('salesAgent')}
            className={`py-2 px-4 rounded flex items-center ${activeView === 'salesAgent' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'}`}
          >
            View Sales Agents
          </button>
          <button
            onClick={() => setActiveView('salesSupervisor')}
            className={`py-2 px-4 rounded flex items-center ${activeView === 'salesSupervisor' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'}`}
          >
            View Sales Supervisors
          </button>
          
          <button
            onClick={() => setActiveView('businessManager')}
            className={`py-2 px-4 rounded flex items-center ${activeView === 'businessManager' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'}`}
          >
            View Business Managers
          </button>
        </div>
      </div>

      {/* Customers Table */}
      {activeView === 'customers' && (
        <div className="bg-white shadow-md rounded-md p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-[#1E1E1E] font-bold text-lg lg:text-xl">Customer List</h1>
          <LoadingButton
            isLoading={isDownloading}
            onClick={downloadExcel}
            className="bg-green-600 text-white py-2 px-4 rounded flex items-center"
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Download Excel
          </LoadingButton>
        </div>

          <table className="table-auto text-left whitespace-no-wrap">
            <thead>
              <tr className="bg-[#F8F8F8] text-gray-600 text-sm uppercase w-full">
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">No</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Customer Name</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Store Name</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">State</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Phone number</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Store Type</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Assigned Agent</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Weekly Purchase</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Date Added</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">View</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm">
           
              {customersData.map((customer, index) => (
                <tr key={customer._id} className="border-b border-gray-200">
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{index + 1}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{customer.firstName} {customer.lastName}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{customer.storeName}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{customer.state}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{customer.phoneNumber}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{customer.storeType}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{customer.fieldagent}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{customer.potentialpurchase}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] ">{new Date(customer.createdAt).toLocaleDateString()}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">
                    <button className="text-blue-600" onClick={() => handleViewAccount(customer)}>
                      View account
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sales Agent Table */}
      {activeView === 'salesAgent' && (
        <div className="bg-white shadow-md rounded p-3 w-fit" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <h1 className="text-[#1E1E1E] font-bold mb-5 text-lg lg:text-xl">Sales Agent Customer List</h1>

          <table className="table-auto text-left whitespace-no-wrap">
           {/* Sales Agent Table */}
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-sm uppercase w-full">
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">No</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Agent Name</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Email</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Role</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm">
              {salesAgentsData.map((agent, index) => (
                <tr key={agent._id} className="border-b border-gray-200">
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{index + 1}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{agent.firstName} {agent.lastName}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{agent.email}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{agent.role}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] flex space-x-2">
                    <LoadingButton 
                      isLoading={loadingStates.deactivate[agent._id]}
                      onClick={() => handleDeactivate(agent._id, 'fieldsalesagents')}
                      className="mr-2 bg-yellow-500 text-white px-2 py-1 rounded min-w-[100px]"
                    >
                      {agent.active ? 'Deactivate' : 'Activate'}
                    </LoadingButton>
                    <LoadingButton 
                      isLoading={loadingStates.delete[agent._id]}
                      onClick={() => handleDelete(agent._id, 'fieldsalesagents')}
                      className="bg-red-500 text-white px-2 py-1 rounded min-w-[80px]"
                    >
                      Delete
                    </LoadingButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sales Supervisor Table */}
      {activeView === 'salesSupervisor' && (
        <div className="bg-white shadow-md rounded p-3 w-fit" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <h1 className="text-[#1E1E1E] font-bold mb-5 text-lg lg:text-xl">Sales Supervisor List</h1>

          <table className="table-auto text-left whitespace-no-wrap">
            {/* Sales Supervisor Table */}
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-sm uppercase w-full">
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">No</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Supervisor Name</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Email</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Role</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm">
              {salesSupervisorsData.map((supervisor, index) => (
                <tr key={supervisor._id} className="border-b border-gray-200">
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{index + 1}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{supervisor.firstName} {supervisor.lastName}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{supervisor.email}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{supervisor.role}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] flex space-x-2">
                    <LoadingButton 
                      isLoading={loadingStates.deactivate[supervisor._id]}
                      onClick={() => handleDeactivate(supervisor._id, 'salessupervisors')}
                      className="mr-2 bg-yellow-500 text-white px-2 py-1 rounded min-w-[100px]"
                    >
                      {supervisor.active ? 'Deactivate' : 'Activate'}
                    </LoadingButton>
                    <LoadingButton 
                      isLoading={loadingStates.delete[supervisor._id]}
                      onClick={() => handleDelete(supervisor._id, 'salessupervisors')}
                      className="bg-red-500 text-white px-2 py-1 rounded min-w-[80px]"
                    >
                      Delete
                    </LoadingButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Business Manager Table */}
      {activeView === 'businessManager' && (
        <div className="bg-white shadow-md rounded-md p-3 w-fit" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <h1 className="text-[#1E1E1E] font-bold mb-5 text-lg lg:text-xl">Business Manager List</h1>

          <table className="table-auto text-left whitespace-no-wrap">
            {/* Business Manager Table */}
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-sm uppercase w-full">
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">No</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Manager Name</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Email</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Role</th>
                <th className="px-3.5 py-1 text-[#1E1E1E] font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm">
              {businessManagersData.map((manager, index) => (
                <tr key={manager._id} className="border-b border-gray-200">
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{index + 1}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{manager.firstName} {manager.lastName}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{manager.email}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E]">{manager.role}</td>
                  <td className="px-3.5 py-1 text-[#1E1E1E] flex space-x-2">
                    <LoadingButton 
                      isLoading={loadingStates.deactivate[manager._id]}
                      onClick={() => handleDeactivate(manager._id, 'businessmanager')}
                      className="mr-2 bg-yellow-500 text-white px-2 py-1 rounded min-w-[100px]"
                    >
                      {manager.active ? 'Deactivate' : 'Activate'}
                    </LoadingButton>
                    <LoadingButton 
                      isLoading={loadingStates.delete[manager._id]}
                      onClick={() => handleDelete(manager._id, 'businessmanager')}
                      className="bg-red-500 text-white px-2 py-1 rounded min-w-[80px]"
                    >
                      Delete
                    </LoadingButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}



{isModalOpen && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-lg p-6 w-1/3">
              <div className="flex justify-between items-center mb-4">
               <h2 className="text-xl font-semibold">Add New {modalRole.replace(/([A-Z])/g, ' $1')}</h2>
                <button onClick={closeModal}>
                  <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Role</label>
                <input
                  type="text"
                  name="role"
                  value={modalRole}
                  readOnly
                  className="w-full px-3 py-2 border rounded bg-gray-100"
                />
              </div>

              {error && <p className="text-red-500">{error}</p>}
              {success && <p className="text-green-500">{success}</p>}

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="bg-blue-600 text-white py-2 px-4 rounded"
                >
                  Submit
                </button>
              </div>
            </form>
            </div>
          </div>
        )}
    </div>

    </>
  );
};

export default AdminCustomerDetails;